import { supabase } from './supabase';

// Schema-cache misses come back from supabase with messages like:
//   "Could not find the table 'public.episode_votes' in the schema cache"
//   "Could not find the table 'public.episode_comments' in the schema cache"
// On a DB that hasn't run the social-features migration yet, these
// helpers should soft-fail (warn + return defaults / pretend success)
// instead of bubbling up red error toasts every time the editor loads.
function isMissingTableError(err) {
  if (!err) return false;
  const msg = (err.message || '').toLowerCase();
  return msg.includes('schema cache')
    || msg.includes('does not exist')
    || err.code === 'PGRST205'
    || err.code === '42P01';
}

// ── Play count ──────────────────────────────────────────────────────────────
// Fire-and-forget. We debounce in the caller so reopening an episode doesn't
// spam the counter.
export async function bumpPlayCount(episodeId) {
  if (!supabase || !episodeId) return;
  try {
    await supabase.rpc('bump_play_count', { p_episode_id: episodeId });
  } catch (err) {
    console.error('bumpPlayCount error:', err);
  }
}

// ── Votes (thumbs up / thumbs down) ────────────────────────────────────────
// value: 1 (up), -1 (down), 0 (clear)
export async function setVote(userId, episodeId, value) {
  if (!supabase || !userId || !episodeId) return null;
  if (value === 0) {
    const { error } = await supabase
      .from('episode_votes')
      .delete()
      .eq('user_id', userId)
      .eq('episode_id', episodeId);
    if (error) {
      if (isMissingTableError(error)) {
        console.warn('[setVote] episode_votes table missing — vote not persisted.');
        return 0;
      }
      console.error('setVote delete error:', error);
      throw error;
    }
    return 0;
  }
  const { error } = await supabase
    .from('episode_votes')
    .upsert({ user_id: userId, episode_id: episodeId, value },
            { onConflict: 'user_id,episode_id' });
  if (error) {
    if (isMissingTableError(error)) {
      console.warn('[setVote] episode_votes table missing — vote not persisted.');
      return value;
    }
    console.error('setVote upsert error:', error);
    throw error;
  }
  return value;
}

// Return the current user's vote on an episode (1, -1, or 0).
export async function getMyVote(userId, episodeId) {
  if (!supabase || !userId || !episodeId) return 0;
  const { data, error } = await supabase
    .from('episode_votes')
    .select('value')
    .eq('user_id', userId)
    .eq('episode_id', episodeId)
    .maybeSingle();
  if (error && !isMissingTableError(error)) {
    console.warn('getMyVote error:', error.message);
  }
  return data?.value ?? 0;
}

// ── Comments ───────────────────────────────────────────────────────────────
export async function getComments(episodeId, limit = 200) {
  if (!supabase || !episodeId) return [];
  const { data, error } = await supabase
    .from('episode_comments')
    .select('id, body, user_id, created_at')
    .eq('episode_id', episodeId)
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) {
    if (!isMissingTableError(error)) console.error('getComments error:', error);
    return [];
  }
  return data || [];
}

export async function addComment(userId, episodeId, body) {
  if (!supabase || !userId || !episodeId) throw new Error('Missing ids');
  const text = (body || '').trim();
  if (!text) throw new Error('Comment body required');
  if (text.length > 2000) throw new Error('Comment too long (2000 char max)');
  const { data, error } = await supabase
    .from('episode_comments')
    .insert({ user_id: userId, episode_id: episodeId, body: text })
    .select('id, body, user_id, created_at')
    .single();
  if (error) {
    if (isMissingTableError(error)) {
      throw new Error('Comments are coming soon — feature not enabled yet.');
    }
    console.error('addComment error:', error);
    throw error;
  }
  return data;
}

export async function deleteComment(userId, commentId) {
  if (!supabase || !userId || !commentId) throw new Error('Missing ids');
  const { data, error } = await supabase
    .from('episode_comments')
    .delete()
    .eq('id', commentId)
    .eq('user_id', userId)
    .select();
  if (error) {
    if (isMissingTableError(error)) {
      throw new Error('Comments are coming soon — feature not enabled yet.');
    }
    console.error('deleteComment error:', error);
    throw error;
  }
  if (!data || data.length === 0) {
    throw new Error('Delete blocked — you can only delete your own comments.');
  }
  return data[0];
}

// ── Related episode lookups (for right-sidebar tabs) ───────────────────────
// Children of an episode — i.e. episodes that were remixed FROM this one.
export async function getRemixesOf(episodeId, limit = 20) {
  if (!supabase || !episodeId) return [];
  const { data, error } = await supabase
    .from('episodes')
    .select('id, topic, format, niche, show_id, user_id, created_at, play_count, like_count, result')
    .eq('remixed_from_id', episodeId)
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) { console.error('getRemixesOf error:', error); return []; }
  return data || [];
}

// Episodes from the same creator (excluding this one).
export async function getEpisodesByCreator(userId, excludeEpisodeId, limit = 20) {
  if (!supabase || !userId) return [];
  let q = supabase
    .from('episodes')
    .select('id, topic, format, niche, show_id, user_id, created_at, play_count, like_count, result')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(limit);
  if (excludeEpisodeId) q = q.neq('id', excludeEpisodeId);
  const { data, error } = await q;
  if (error) { console.error('getEpisodesByCreator error:', error); return []; }
  return data || [];
}

// Best-effort "Similar" — same niche or same format, excluding the source.
// Keeps things fast without a proper recommendation system.
export async function getSimilarEpisodes(episode, limit = 20) {
  if (!supabase || !episode?.id) return [];
  const { data, error } = await supabase
    .from('episodes')
    .select('id, topic, format, niche, show_id, user_id, created_at, play_count, like_count, result')
    .neq('id', episode.id)
    .or([
      episode.niche ? `niche.eq.${episode.niche}` : '',
      episode.format ? `format.eq.${episode.format}` : '',
    ].filter(Boolean).join(','))
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) { console.error('getSimilarEpisodes error:', error); return []; }
  return data || [];
}

// ── Public discovery (for Search / Explore) ────────────────────────────────
// Most-recent episodes across all users (capped). v1 — no admin curation yet.
export async function getFeaturedEpisodes(limit = 9) {
  if (!supabase) return [];
  const { data, error } = await supabase
    .from('episodes')
    .select('id, topic, format, niche, show_id, user_id, created_at, play_count, like_count, result')
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) { console.error('getFeaturedEpisodes error:', error); return []; }
  return data || [];
}

export async function getFeaturedShows(limit = 10) {
  if (!supabase) return [];
  const { data, error } = await supabase
    .from('shows')
    .select('id, name, description, brand_color, cover_art_url, user_id, created_at, episode_count')
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) { console.error('getFeaturedShows error:', error); return []; }
  return data || [];
}

// ── Global search ──────────────────────────────────────────────────────────
// Three parallel queries fanned out from one search term. Each helper does
// an OR'd ilike across the columns that make sense for that table. ilike is
// a substring match (case-insensitive) — fine for v1 discovery; we can swap
// in pg_trgm or full-text if relevance becomes a real problem.
//
// All three respect the user's RLS policies — anything not publicly
// readable (private profiles, draft episodes) is filtered server-side.

function escapeIlike(s) {
  // PostgREST splits comma-separated `.or()` args by commas, so a literal
  // comma in the search term would shatter the query. Strip control chars
  // and anything that'd confuse PostgREST's parser.
  return String(s || '').replace(/[%(),]/g, ' ').trim();
}

export async function searchCreators(q, limit = 12) {
  if (!supabase) return [];
  const term = escapeIlike(q);
  if (!term) return [];
  const pat = `%${term}%`;
  // Only surface profiles that opted in to the marketplace — keeps
  // people who explicitly toggled themselves off (private accounts)
  // out of discovery search.
  const { data, error } = await supabase
    .from('podcaster_profiles')
    .select('user_id, full_name, username, show_name, niche, avatar_url, verified, bio, professional_title')
    .eq('marketplace_visible', true)
    .or(
      [
        `full_name.ilike.${pat}`,
        `username.ilike.${pat}`,
        `show_name.ilike.${pat}`,
        `niche.ilike.${pat}`,
        `bio.ilike.${pat}`,
        `professional_title.ilike.${pat}`,
      ].join(',')
    )
    .limit(limit);
  if (error) { console.error('searchCreators error:', error); return []; }
  return data || [];
}

export async function searchShows(q, limit = 12) {
  if (!supabase) return [];
  const term = escapeIlike(q);
  if (!term) return [];
  const pat = `%${term}%`;
  const { data, error } = await supabase
    .from('shows')
    .select('id, name, description, brand_color, cover_art_url, user_id, created_at, episode_count')
    .or([`name.ilike.${pat}`, `description.ilike.${pat}`].join(','))
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) {
    // The shows table is optional on older DBs — soft-fail instead of
    // breaking the whole search.
    if (!isMissingTableError(error)) console.error('searchShows error:', error);
    return [];
  }
  return data || [];
}

export async function searchEpisodes(q, limit = 12) {
  if (!supabase) return [];
  const term = escapeIlike(q);
  if (!term) return [];
  const pat = `%${term}%`;
  // result is a jsonb column; ilike on the stringified jsonb catches
  // matches in title, hook, segments, etc. without us having to spell
  // out every nested key.
  const { data, error } = await supabase
    .from('episodes')
    .select('id, topic, format, niche, show_id, user_id, created_at, play_count, like_count, result')
    .or(
      [
        `topic.ilike.${pat}`,
        `niche.ilike.${pat}`,
        `format.ilike.${pat}`,
        `result.cs.{"title":"${term}"}`, // exact-ish title match in jsonb
      ].join(',')
    )
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) {
    // The `result.cs.{...}` filter can 400 if the jsonb shape doesn't
    // match what the column actually stores. Retry with just the
    // direct-column filters so a bad jsonb pattern never kills search.
    const retry = await supabase
      .from('episodes')
      .select('id, topic, format, niche, show_id, user_id, created_at, play_count, like_count, result')
      .or([`topic.ilike.${pat}`, `niche.ilike.${pat}`, `format.ilike.${pat}`].join(','))
      .order('created_at', { ascending: false })
      .limit(limit);
    if (retry.error) {
      console.error('searchEpisodes error:', retry.error);
      return [];
    }
    return retry.data || [];
  }
  return data || [];
}
